import React, { useEffect, useRef, useState, forwardRef, useImperativeHandle, useMemo } from 'react';
import anychart from 'anychart';
import { Row, Space, Card, Tooltip, Badge } from 'antd';
import {v4 as uuidv4} from 'uuid';
import RangeSelector from "./StockChart/RangeSelector";
import {extractRangeProperties, setDataGrouping} from "./StockChart/tools";
import {stylePlot, styleScroller, styleLegend, finishDrawingChart} from "./StockChart/styles";
import {fillSeriesFromConfig, handleSeriesItem} from "./StockChart/seriesUtils";
import NivoBarChart from "./nivo/NivoBarChart";
import debounce from 'lodash/debounce';
import {convertToBarData, filterInRange} from "./StockChart/dataTools";

import useStockChart from "./StockChart/useStockChart";
import useAnychartAnnotations from "./StockChart/useAnychartAnnotations";


export const formatPlotScale = (plot, config)=>{
    if (config.yScale) {
        plot.yScale().comparisonMode(config.yScale);
      }
      if (config.stack){
          plot.yScale().stackMode('value');
      }
      if (config.yMax){
          plot.yScale().maximum(config.yMax);
      }
}





/**
 *
 * @param dataSeries [
 *        {
 *        plot: <index>,
 *          data: [
 *            {
 *              name: <series name>,
 *              type: <series type> *'line', 'stepLine', 'area'*,
 *              data: <series data: [[<timestamp>, <series1Value>, ...], [...], ...]>,
 *              **Optional**
 *              yScale: <'percentage', 'log'>,
 *              stroke: <color>,
 *            }
 *         ]
 *       }
 * ]
 * @param width
 * @param height
 * @param title
 * @param xTicks {number} - Number of ticks to show on the x-axis
 * @param customSnapshotCallback {function} - Callback function to handle custom snapshot data
 * @param allowScreenshot {boolean} - Flag to determine if the screenshot button should be shown
 * @param _recursive {boolean} - Internal flag to determine if the chart is being rendered recursively
 * @param showRangeSelector {boolean} - Flag to determine if the range selector should be shown
 * @param allowChartInteractions {boolean} - Flag to determine if the chart should allow interactions
 * @param callbackOnCreateWindow {function} - Callback function to handle creating a new window.
 *                                            ...It needs to accept 'dataSeries' as an argument with the data
 *                                            ...for each series in the chart truncated to the selected range
 *
 * @returns {JSX.Element}
 * @constructor
 */
const StockChart = forwardRef(({ dataSeries,
                      width = '100%',
                      height = 600,
                      title = "",
                      xTicks=40,
                      allowScreenshot=true,
                      customSnapshotCallback=null,
                      showRangeSelector=true,
                      allowChartInteractions=true,
                      callbackOnCreateWindow=null,
                      callbackOnAnnotationSelect=null,
                      callbackOnSelectedPoints=null,
                      callbackOnRangeChange=null,
                      disableAnnotations=false,
                      disableTooltip=false,
                      xPadding=50,
                      yPadding=10,
                      extraMiddle=null,
                      extraToolsLeft=null,
                      extraToolsRight=null,
                      _recursive=false,
                      disableLegend=false,
                      interactive=true,
                      theme='light',
                      legendDirection='horizontal',
                                   rangeSelectorRowClassName=''

                    }, ref) => {
    const chartContainerRef = useRef(null);
    const chartRef = useRef(null);
    const seriesListRef = useRef([]);

    const controllerRef = useRef(null);
    const [baseId] = useState(uuidv4());
    const [containerId] = useState(`chart-container-${baseId}`); // Generate a unique ID for the container
    const [rangeProps, setRangeProps] = useState(undefined);// [min, max, years
    const {setAnnotationsJson, drawingAnnotation} = useAnychartAnnotations(controllerRef, chartRef);
    const [chartConfig, setChartConfig] = useState({
        disableTooltip,
        disableLegend,
        legendDirection,
        theme,
        zoomWheel: interactive,
        scrollWheel: interactive,
        view: 'stock'
    });
    const [selectedRange, setSelectedRange] = useState({ start: null, end: null });
    const [lastHoveredTimestamp, setLastHoveredTimestamp] = useState(null);
    const [lastHoveredPoint, setLastHoveredPoint] = useState(null);
    const [slidingRange, setSlidingRange] = useState({ start: null, end: null });


    const listenForRangeChanges = ()=>{
        if (!chartRef.current) return;
        chartRef.current.listen('selectedrangechangefinish', (e) => {
            const newRange = {start: e.firstSelected, end: e.lastSelected};
            console.log('newRange', newRange);
            setSelectedRange(newRange);

            // callback?.(newRange.start, newRange.end);
        });
    }
    const getIndexOnClick = (event)=>{
        return event.seriesStatus?.[0].nearestPointToCursor.index
    }


    useImperativeHandle(ref, () => ({
        selectRange: (start, end) => {
            if (chartRef.current) {
                try {
                    setSelectedRange({start, end});
                    chartRef.current.selectRange(start, end);
                    callbackOnRangeChange?.(start, end);
                } catch (e) {
                }
            }
        },
        clearAnnotations: () => {
            if (controllerRef.current) {
                controllerRef.current.removeAllAnnotations();
            }
            setAnnotationsJson(null);

            return ref?.current;
        },
        clearSelectedPoints: () => {
            return ref?.current;
        }

    }));


    const handleDoubleClick = (plot, seriesList) => {
        plot.listen('dblClick', (e) => {
            const clickIndex = getIndexOnClick(e);
            const items = seriesList.map(s => {
                return {
                    name: s.series.name(),
                    data: s.data?.[clickIndex] ?? []
                }
            });
            const timestamp = items[0].data[0];
        });
    }


    useEffect(() => {
        setRangeProps(extractRangeProperties(dataSeries))
    }, [dataSeries]);

    const callbackOnMousemove = ()=>{
        if (!chartRef.current || !customSnapshotCallback) return;

        const chart = chartRef.current;

        const handleMouseMove = (event) => {
            const seriesStatus = event.seriesStatus;
            const closestIndex = seriesStatus?.[0]?.nearestPointToCursor?.index;
            if (closestIndex !== undefined) {
                // Use the seriesList from the ref
                const selectedPoints = seriesListRef.current.map((series) => {
                    return {
                        name: series.series.name(),
                        data: series.data[closestIndex]
                    };
                });
                const snapshotPoint = selectedPoints.find((point) => point.data[2])?.data;
                customSnapshotCallback?.(snapshotPoint?.[2], snapshotPoint?.[0]);
            }
        };

        chart.listen('mouseMove', handleMouseMove);

    }


    useEffect(() => {
        if (!dataSeries || dataSeries.length === 0) return;
        if (chartRef.current) {
            chartRef.current.dispose();
            chartRef.current = null;
        }
        let chart = anychart.stock();
        chartRef.current = chart;
        const seriesList = [];
        let scrollerDataMapping;

        dataSeries.forEach((plotConfig, plotIndex) => {
            const plot = chart.plot(plotConfig.plot);
            stylePlot(plot, plotConfig, chartConfig.theme); // Assume this function styles the plot as needed
            plotConfig.data.forEach((seriesConfig, seriesIndex) => {
                const {dataTable, mapping} = handleSeriesItem(seriesConfig.data, seriesConfig);
                const dataMapping = dataTable.mapAs(mapping);

                const series = fillSeriesFromConfig(seriesConfig, plot, dataMapping, lastHoveredTimestamp,
                    setLastHoveredTimestamp, setLastHoveredPoint, !!customSnapshotCallback, chartConfig.disableTooltip);
                seriesList.push({series, plotIndex, data: seriesConfig.data});
                if (!scrollerDataMapping && !plotConfig?.disableScrollerChart) {
                    scrollerDataMapping = dataTable.mapAs({value: 1});
                }
            });
            if (!chartConfig.disableLegend) {
                styleLegend(plot, plotConfig ?? {}, chartConfig.legendDirection, chartConfig.theme);
            } else {
                plot.legend().enabled(false);
            }
            formatPlotScale(plot, plotConfig);
            listenForRangeChanges(callbackOnRangeChange);
            handleDoubleClick(plot, seriesList)
            chart.interactivity().zoomOnMouseWheel(true);
            chart.interactivity().scrollOnMouseWheel(true);
            setDataGrouping({chart, enabled: true});

            if (scrollerDataMapping) {
                styleScroller(chart, scrollerDataMapping, dataSeries[0], chartConfig.theme);
            }
            if (selectedRange.start || selectedRange.end){
                chart.selectRange(selectedRange.start, selectedRange.end);
                // callbackOnRangeChange?.(selectedRange.start, selectedRange.end);
            }
            finishDrawingChart(chart, xTicks, chartContainerRef.current, title, xPadding, yPadding, chartConfig.theme);
            seriesListRef.current = seriesList;
            // updateChartLegend(chart, dataSeries, chartConfig);
            // updateChartPlotStyles(chart, dataSeries, chartConfig);
            return () => {
                if (chartRef.current) {
                    chartRef.current.dispose();
                    controllerRef.current = null;
                }
            }
        });
    }, [dataSeries, selectedRange]);
    /**
     * Update the data only
     */
    // useEffect(() => {
    //     if (!chartRef.current) return;
    //
    //
    //     // Loop through the dataSeries and update each series accordingly
    //     dataSeries.forEach((plotConfig, plotIndex) => {
    //         const plot = chartRef.current.plot(plotIndex);
    //
    //         plotConfig.data.forEach((seriesConfig, seriesIndex) => {
    //             if (seriesListRef.current[plotIndex]?.series) {
    //                 // Update data for the existing series
    //                 const newDataTable = anychart.data.table(); // Assuming you have a utility function to create dataTable
    //                 newDataTable.addData(seriesConfig.data);
    //                 seriesListRef.current[plotIndex].series.data(newDataTable);
    //             } else {
    //                 // If a new series was added, add it to the plot
    //                 const { dataTable, mapping } = handleSeriesItem(seriesConfig.data, seriesConfig);
    //                 const dataMapping = dataTable.mapAs(mapping);
    //                 const newSeries = fillSeriesFromConfig(seriesConfig, plot, dataMapping);
    //                 seriesListRef.current.push({ series: newSeries, plotIndex, data: seriesConfig.data });
    //             }
    //         });
    //     });
    //     // updateChartLegend(chartRef.current, dataSeries, chartConfig);
    //     // updateChartPlotStyles(chartRef.current, dataSeries, chartConfig);
    //
    // }, [dataSeries]);

    useEffect(() => {
        if (!chartRef.current) return;

        const chart = chartRef.current;

        chart.getPlotsCount = () => dataSeries.length; // Ensures we can access the plot count properly
        // Update legend settings
        for (let i = 0; i < chart.getPlotsCount(); i++) {
            const plot = chart.plot(i);
            stylePlot(plot, dataSeries[i], chartConfig.theme);
        }

        // Update interaction settings
        chart.interactivity().zoomOnMouseWheel(chartConfig.zoomWheel);
        chart.interactivity().scrollOnMouseWheel(chartConfig.scrollWheel);

    }, [chartConfig]);
    // New useEffect for mouseMove event listener
    useEffect(() => {
        if (!chartRef.current || !customSnapshotCallback) return;

        const chart = chartRef.current;

        // Debounced version of the customSnapshotCallback
        const debouncedMouseMoveCallback = debounce((snapshotPoint) => {
            customSnapshotCallback?.(snapshotPoint?.[2], snapshotPoint?.[0]);
        }, 200); // Adjust the delay (200ms) as needed for performance

        const handleMouseMove = (event) => {
            const seriesStatus = event.seriesStatus;
            const closestIndex = seriesStatus?.[0]?.nearestPointToCursor?.index;
            if (closestIndex !== undefined) {
                // Use the seriesList from the ref
                const selectedPoints = seriesListRef.current.map((series) => {
                    return {
                        name: series.series.name(),
                        data: series.data[closestIndex]
                    };
                });
                const snapshotPoint = selectedPoints.find((point) => point.data[2])?.data;

                // Trigger the debounced callback
                debouncedMouseMoveCallback(snapshotPoint);
            }
        };

        chart.listen('mouseMove', handleMouseMove);

        // Cleanup the listener and debounced callback when component unmounts
        return () => {
            chart.unlisten('mouseMove', handleMouseMove);
            debouncedMouseMoveCallback.cancel(); // Cancel pending debounced calls to avoid memory leaks
        };
    }, [customSnapshotCallback, dataSeries, selectedRange]);
    return (
            <>
                <Card
                    bordered={false}
                    className={'gapless sunken-shadow-2'}
                >
                    {rangeProps && showRangeSelector ? (
                        <div style={{paddingLeft: '60px'}} className={rangeSelectorRowClassName}>
                            <RangeSelector
                                {...rangeProps}
                                onYearSelected={setSelectedRange}
                                baseId={baseId}
                                selectedRange={selectedRange}
                                extraMiddle={extraMiddle}
                                chartConfig={chartConfig}
                                setChartConfig={setChartConfig}
                                extra={
                                    <Space>
                                        {extraToolsLeft}
                                        {extraToolsRight}
                                    </Space>
                                }
                            />
                        </div>

                    ) : null}
                    {chartConfig.view === 'stock' && (
                        <div className={'stock-chart'}
                             id={containerId}
                             ref={chartContainerRef}
                             style={{width, height}}
                        />
                    )}
                    {chartConfig.view==='bar' && (
                        <div style={{height: height}}>
                            <NivoBarChart {...convertToBarData(dataSeries, selectedRange)}
                                          vertical={false}
                                          barPadding={0.2}
                                          mode={'stacked'}
                            />
                        </div>
                    )}
                </Card>
            </>
        );
});

export default StockChart;
